import { PROJECT_NAME } from '../project/PROJECT_NAME';
import { COMPANY_EMAIL } from '../contact/COMPANY_EMAIL';
import { BUSINESS_LEGAL_NAME } from './BUSINESS_LEGAL_NAME';
import { LEGAL_CONTACTS } from './LEGAL_CONTACTS';

/**
 * @group Legal
 * @summary <blockquote>Comprehensive Terms of Service content (structured) for use in web/mobile.</blockquote>
 *
 * @remarks
 * This structured terms object centralizes legal content so it can be rendered
 * consistently across products and channels without hardcoded duplication.
 */
export const TERMS_OF_SERVICE = {
  metadata: {
    title: 'Terms of Service',
    effectiveDate: '2026-02-26',
    lastUpdated: '2026-02-26',
    articleAriaLabel: `${PROJECT_NAME} terms of service`,
    introduction: `These Terms apply to your use of ${PROJECT_NAME}.`,
    privacyPolicyPath: '/privacy',
  },
  emergencyDisclaimer: {
    title: 'STOP - Emergency Warning',
    messages: [
      'If you have chest pain, severe bleeding, severe breathing difficulty, stroke symptoms, loss of consciousness, or another life-threatening emergency, leave this app and call 112 or 10111 immediately.',
      `${PROJECT_NAME} is for non-urgent care and is not an emergency service.`,
    ],
  },
  keyPoints: [
    'Phila is for non-urgent care only. If this is an emergency, call 112 or 10111 immediately.',
    `${PROJECT_NAME} provides technology to connect you with independent healthcare professionals (HCPs).`,
    'Informed telehealth consent is required and documented for every consultation.',
    'Prescriptions are subject to clinical judgment and legal limits. No Schedule 5 or higher medicines are prescribed.',
    'Consultations are generally paid upfront. You may receive invoices for medical aid claim-back where applicable.',
    'Your health information is handled in line with POPIA and shared only for treatment, operations, and legal duties.',
  ],
  sections: [
    {
      id: 'agreement-definitions',
      title: '1. Agreement and Definitions',
      paragraphs: [
        {
          text: `These Terms of Service ("Terms") govern your use of ${PROJECT_NAME}'s public website, app, and telehealth platform services.`,
        },
        {
          text: `These Terms are an agreement between you and ${BUSINESS_LEGAL_NAME} ("Phila", "we", "us"), unless a separate written agreement applies.`,
        },
        {
          text: 'By creating an account, booking a consultation, or continuing to use the platform, you confirm that you have read and accepted these Terms and our Privacy Policy.',
        },
      ],
      bullets: [
        '"Platform" means the technology service that enables bookings, communications, payments, and related support.',
        '"Consultation" means a remote clinical interaction between you and an HCP through chat, voice, or video.',
        '"HCP" means an independent, appropriately registered healthcare professional (for example, an HPCSA-registered doctor).',
        '"Clinical Record" means the healthcare notes and records generated by the HCP during your care.',
      ],
    },
    {
      id: 'nature-of-service',
      title: '2. Nature of Service and Clinical Relationship',
      paragraphs: [
        {
          text: `${PROJECT_NAME} provides technology that connects users to independent HCPs. ${PROJECT_NAME} does not practice medicine and does not replace in-person healthcare where needed.`,
        },
        {
          text: 'When you consult with an HCP through the platform, a direct clinician-patient relationship is formed between you and that HCP.',
        },
      ],
      bullets: [
        'The HCP is responsible for clinical decisions, diagnosis, treatment, and any prescriptions they issue.',
        'Phila is responsible for platform operations, payment processing support, and user experience tooling.',
        'Phila does not guarantee a specific clinical outcome, diagnosis, or treatment plan.',
      ],
    },
    {
      id: 'eligibility-territory',
      title: '3. Eligibility, Minors, and Territory',
      paragraphs: [
        { text: 'You must use the platform lawfully and provide accurate information.' },
        {
          text: 'Telehealth services are designed primarily for users physically located in South Africa at the time of consultation.',
        },
      ],
      bullets: [
        'You must be 18 years or older to use the service independently.',
        'If the patient is a minor, a parent or legal guardian must provide verified consent and remain responsible for platform use.',
        'We may request identity or guardian verification to protect patient safety and comply with legal obligations.',
      ],
    },
    {
      id: 'emergency-telehealth-limitations',
      title: '4. Emergency Disclaimer and Telehealth Limitations',
      paragraphs: [
        {
          text: `${PROJECT_NAME} is not an emergency service. Telehealth has limitations and may not be suitable for every condition.`,
        },
        {
          text: 'If your symptoms may require urgent in-person care, the HCP may direct you to an emergency department or in-person provider.',
        },
      ],
      bullets: [
        'Do not use this platform for life-threatening or urgent emergencies.',
        'Service quality may be affected by your device, connectivity, or load shedding and network interruptions.',
        'Where remote assessment is not clinically sufficient, the HCP may refuse to continue remotely and recommend in-person care.',
      ],
    },
    {
      id: 'informed-consent',
      title: '5. Informed Consent for Telehealth (HPCSA Booklet 10 Alignment)',
      paragraphs: [
        {
          text: 'Before and during each consultation, informed consent to telehealth is required. Consent is not a one-time checkbox and may be confirmed again when clinically necessary.',
        },
        {
          text: 'By proceeding with a consultation, you acknowledge that telehealth benefits, limitations, alternatives, and privacy considerations have been explained in a manner reasonably suitable for informed decision-making.',
        },
      ],
      bullets: [
        'You consent to remote history taking, remote assessment, and clinical documentation.',
        'You may withdraw consent and stop a consultation at any time, but this may limit what care can safely be provided remotely.',
        'The HCP may pause or end the consultation if consent is unclear, safety is at risk, or identity cannot be reasonably verified.',
      ],
    },
    {
      id: 'ai-assisted-features',
      title: '6. AI-Assisted Features (HPCSA Booklet 20 Alignment)',
      paragraphs: [
        {
          text: 'Some platform features may use AI-assisted tools such as triage support, symptom summarization, or workflow automation.',
        },
        { text: 'AI tools are assistive only and do not replace professional judgment.' },
      ],
      bullets: [
        'Final clinical decisions are made by a qualified HCP.',
        'AI-generated suggestions may be incomplete or incorrect and must be clinically reviewed by the HCP.',
        'You may request escalation to human support where available.',
      ],
    },
    {
      id: 'scripts-notes',
      title: '7. Prescriptions, Medical Certificates, and Referrals',
      paragraphs: [
        {
          text: 'Prescriptions, sick notes, and referrals are issued only where clinically appropriate and legally permitted.',
        },
        {
          text: 'No right to receive a prescription, certificate, or referral is created by booking or paying for a consultation.',
        },
      ],
      bullets: [
        'Where lawful and clinically appropriate, only eligible medicines may be prescribed.',
        'Pharmacies may apply their own dispensing checks and may refuse to dispense.',
        'Medical certificates are clinical documents and may not be accepted by every employer, school, or third party.',
        'Phila is not responsible for employment, attendance, or insurance disputes arising from medical certificates.',
      ],
    },
    {
      id: 'fees-refunds',
      title: '8. Fees, Billing, No-Shows, and Refunds',
      paragraphs: [
        { text: 'Consultation fees and any applicable charges are displayed before checkout.' },
        {
          text: 'Unless explicitly stated otherwise, consultations are cash-first and payable before service delivery.',
        },
      ],
      bullets: [
        'For scheduled consultations, cancellation requests made at least 24 hours before the booking time are generally eligible for refund or credit, subject to payment provider costs.',
        'For on-demand or instant-chat consultations, no refund is generally available once the consultation has started.',
        'If a consultation cannot begin due to a platform-side technical failure, we will assess and may provide a credit or refund.',
        'We may update pricing, but material pricing changes will be communicated before they apply to new bookings.',
      ],
    },
    {
      id: 'medical-aid-claims',
      title: '9. Medical Aid and Claim-Back',
      paragraphs: [
        {
          text: 'Unless explicitly stated as a covered benefit, platform consultations are typically paid by you directly.',
        },
        {
          text: 'Where available, we provide invoices or supporting documentation to help you submit a claim to your medical aid.',
        },
      ],
      bullets: [
        'Claim outcomes are determined by your medical scheme rules and not by Phila.',
        'Phila does not guarantee reimbursement, co-payment levels, or benefit eligibility.',
      ],
    },
    {
      id: 'privacy-popia-records',
      title: '10. POPIA, Data Sharing, and Clinical Record Ownership',
      paragraphs: [
        {
          text: 'Health information is special personal information under POPIA and is handled with enhanced safeguards.',
        },
        {
          text: 'By using the platform, you consent to the transfer of relevant information required for your treatment and care operations.',
        },
      ],
      bullets: [
        'Information may be shared with your treating HCPs and, where necessary, laboratories, pharmacies, referral providers, and regulated care partners.',
        'Detailed processing terms are set out in our Privacy Policy.',
        'Clinical Records are created and owned by the treating HCP or their practice, while Phila may host or process records on their instructions.',
        'Records are retained in line with applicable legal and professional obligations (including healthcare retention requirements).',
        `For privacy requests, contact ${LEGAL_CONTACTS.dataProtectionOfficerEmail}.`,
      ],
    },
    {
      id: 'user-responsibilities',
      title: '11. User Responsibilities and Prohibited Conduct',
      paragraphs: [
        {
          text: 'You are responsible for keeping your account credentials secure and for activities under your account.',
        },
        { text: 'You must use the platform respectfully and lawfully.' },
      ],
      bullets: [
        'Do not provide false, misleading, or incomplete medical information.',
        'Do not harass HCPs, support staff, or other users.',
        'Do not seek controlled medication through deception, repeated duplicate requests, or doctor shopping behavior.',
        'Do not attempt to interfere with platform security, availability, or integrity.',
      ],
    },
    {
      id: 'service-availability',
      title: '12. Service Availability and Third-Party Dependencies',
      paragraphs: [
        {
          text: 'We work to keep the platform available and secure, but uninterrupted availability cannot be guaranteed.',
        },
        {
          text: 'Some services depend on third parties (for example payment processors, telecom providers, pharmacies, or laboratory partners).',
        },
      ],
      bullets: [
        'Planned maintenance and urgent fixes may temporarily affect access.',
        'Third-party terms may apply to external services and products.',
        'Phila is not responsible for outages or failures caused solely by third-party systems outside our reasonable control.',
      ],
    },
    {
      id: 'liability',
      title: '13. Liability, Warranties, and Risk Allocation',
      paragraphs: [
        { text: 'The platform is provided on an "as available" basis to the extent allowed by law.' },
        {
          text: 'Nothing in these Terms limits rights that cannot lawfully be excluded under South African law, including applicable consumer protections.',
        },
      ],
      bullets: [
        'Phila is responsible for platform-related operations and technical services we directly control.',
        'The treating HCP is responsible for clinical care and professional conduct during consultations.',
        'To the extent permitted by law, Phila is not liable for indirect or consequential loss, including loss of profits or goodwill.',
        'You should seek immediate in-person care where symptoms worsen, change, or become urgent.',
      ],
    },
    {
      id: 'termination',
      title: '14. Suspension and Termination',
      paragraphs: [
        { text: 'You may stop using the platform at any time.' },
        {
          text: 'We may suspend or terminate access where required for safety, legal compliance, or serious Terms breaches.',
        },
      ],
      bullets: [
        'Reasons may include fraud, abusive behavior, misuse of prescriptions, or repeated policy violations.',
        'Where reasonably possible, we will provide notice and an explanation before permanent termination.',
        'Termination does not remove obligations already incurred, including valid completed consultation fees.',
      ],
    },
    {
      id: 'changes-to-terms',
      title: '15. Changes to These Terms',
      paragraphs: [
        {
          text: 'We may update these Terms to reflect legal, regulatory, clinical governance, or service changes.',
        },
        {
          text: 'Material changes will be communicated with reasonable prior notice on the website or app.',
        },
      ],
      bullets: [
        'If you do not agree to updated Terms, you must stop using the platform.',
        'Continued use after the effective date of updated Terms constitutes acceptance of the updated Terms.',
      ],
    },
    {
      id: 'complaints-disputes',
      title: '16. Complaints and Dispute Resolution',
      paragraphs: [
        {
          text: 'If you have a complaint, contact us first so we can investigate and resolve it promptly.',
        },
        {
          text: 'Clinical complaints may also be directed to the relevant professional regulator where applicable.',
        },
      ],
      bullets: [
        `Support and legal contact: ${COMPANY_EMAIL}`,
        `Information Regulator (South Africa): ${LEGAL_CONTACTS.regulatoryContacts.southAfrica.informationRegulatorEmail}`,
        'Nothing in these Terms removes your right to approach statutory bodies, ombuds, or courts where legally available.',
      ],
    },
    {
      id: 'governing-law',
      title: '17. Governing Law',
      paragraphs: [
        { text: 'These Terms are governed by the laws of the Republic of South Africa.' },
        {
          text: 'Any dispute not resolved informally will be handled through appropriate South African forums with jurisdiction.',
        },
      ],
    },
    {
      id: 'contact',
      title: '18. Contact Details',
      paragraphs: [
        { text: `Operator: ${BUSINESS_LEGAL_NAME}` },
        { text: `Email: ${COMPANY_EMAIL}` },
      ],
    },
  ],
} as const;
